import React from 'react';

import {configureMapColumnNamesModalPane} from 'bulk-import/panes/MapColumnNamesModalPane';
import ShowErrorsModalPane from 'bulk-import/panes/ShowErrorsModalPane';
import StorableImportSettingsModalPane from 'bulk-import/panes/StorableImportSettingsModalPane';
import UploadSpreadsheetModalPane from 'bulk-import/panes/UploadSpreadsheetModalPane';
import * as SpreadsheetBulkImportActions from 'bulk-import/SpreadsheetBulkImportActions';
import {
  COLUMN_TYPE,
  IMPORT_TYPE,
  Pane,
  PaneKey,
  SelectedSchemaIds,
  SPREADSHEET_BULK_IMPORT_KEY,
  SpreadsheetColumn,
  STORABLE_BASE_COLUMN_TYPES,
} from 'bulk-import/SpreadsheetBulkImportConstants';
import SpreadsheetBulkImportModalContainer from 'bulk-import/SpreadsheetBulkImportModalContainer';
import * as SpreadsheetBulkImportUtil from 'bulk-import/SpreadsheetBulkImportUtil';
import {getLocationCapacityConfigColumns} from 'bulk-import/storables/StorableColumnsUtil';
import {Registry} from 'constants/RegistryConstants';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import LocationSchemaSelector from 'samples/LocationSchemaSelector';
import runModal from 'ui-library/runModal';

export interface BulkUpdateLocationsFlowProps {
  initialRegistry: Registry | null;
  onRequestClose: () => void;
  actionOrigin: ActionOrigin;
}

export function openBulkUpdateLocationsFlow(
  props: Omit<BulkUpdateLocationsFlowProps, 'onRequestClose'>
): void {
  runModal(BulkUpdateLocationsFlow, {
    ...props,
    onRequestClose: SpreadsheetBulkImportActions.closeModal,
    modalKey: SPREADSHEET_BULK_IMPORT_KEY,
  });
}

export default function BulkUpdateLocationsFlow(props: BulkUpdateLocationsFlowProps): JSX.Element {
  const getPanes = (): Array<Pane> => [
    {
      paneKey: PaneKey.SET_STORABLE_IMPORT_SETTINGS,
      configuration: StorableImportSettingsModalPane.configure({
        schemaSelectorClass: LocationSchemaSelector,
        schemaIdAccessorKey: 'locationSchemaId',
        showProjectSelector: false,
      }),
    },
    {
      paneKey: PaneKey.UPLOAD_SPREADSHEET,
      configuration: UploadSpreadsheetModalPane.configure({
        getExampleImageURL: () => '/static/img/spreadsheet-import-example-update-locations.png',
      }),
    },
    {
      paneKey: PaneKey.MAP_COLUMN_NAMES,
      configuration: configureMapColumnNamesModalPane({
        getValidColumnTypes: (): Array<COLUMN_TYPE> => [
          ...STORABLE_BASE_COLUMN_TYPES,
          COLUMN_TYPE.LOCATION_SCHEMA_FIELD,
          ...getLocationCapacityConfigColumns(),
          COLUMN_TYPE.NEW_BARCODE,
          COLUMN_TYPE.IGNORE,
        ],
        getRequiredColumnsError(
          columns: Array<SpreadsheetColumn>,
          {selectedSchemaIds}: {selectedSchemaIds: SelectedSchemaIds}
        ): string | null {
          const requiredColumnTypes = [COLUMN_TYPE.BARCODE];
          const requiredFieldError = SpreadsheetBulkImportUtil.getRequiredFieldError(
            requiredColumnTypes,
            columns,
            selectedSchemaIds,
            IMPORT_TYPE.UPDATE_LOCATIONS
          );
          if (requiredFieldError) {
            return requiredFieldError;
          }
          if (!columns.some(({type}) => type !== COLUMN_TYPE.BARCODE && type !== COLUMN_TYPE.IGNORE)) {
            return 'At least one other field is required.';
          }
          return null;
        },
        onValidateSpreadsheet: SpreadsheetBulkImportActions.validateSpreadsheetForUpdateLocations,
      }),
    },
    {
      paneKey: PaneKey.SHOW_ERRORS,
      configuration: ShowErrorsModalPane.configure({
        enableReplaceLinkWithText: true,
        enableSkipErrors: true,
      }),
    },
  ];

  return (
    <SpreadsheetBulkImportModalContainer
      importItemWord="location"
      importType={IMPORT_TYPE.UPDATE_LOCATIONS}
      initialSettings={{initialRegistry: props.initialRegistry}}
      onImportFromSpreadsheet={SpreadsheetBulkImportActions.updateLocationsFromSpreadsheetWithDataMods}
      analyticsMetadata={{actionOrigin: props.actionOrigin}}
      onRequestClose={props.onRequestClose}
      panes={getPanes()}
      title="Bulk update locations"
    />
  );
}
